import { z, ZodType } from "zod";

import { looseUuid } from "@thalamos/common";

import {
  OrganisationIdSchema,
  type OrganisationIdUnbranded,
} from "./organisation.js";

export const UserDirectoryKeysValues = ["upn", "samAccountName"] as const;
export type UserDirectoryKeys = (typeof UserDirectoryKeysValues)[number];
export const UserDirectoryKeysSchema = z.enum({
  UserPrincipalName: "upn",
  SamAccountName: "samAccountName",
} satisfies Record<string, UserDirectoryKeys>);

export type UserDirectoryInstanceIdUnbranded = string;
export const UserDirectoryInstanceIdSchema =
  looseUuid().brand<"vision-commmon:UserDirectoryInstanceId">() satisfies ZodType<UserDirectoryInstanceIdUnbranded>;
export type UserDirectoryInstanceId = z.infer<
  typeof UserDirectoryInstanceIdSchema
>;

export type UserDirectoryInstanceUnbranded = {
  id: UserDirectoryInstanceIdUnbranded;
  organisationId: OrganisationIdUnbranded;
  tenantId?: string;
  clientId?: string;
  clientSecret?: string;
  accessToken: string;
  accessTokenExpires: Date;
  userDirectoryKey: UserDirectoryKeys;
  auth0ConnectionId?: string;
  supervisorMapping?: string[];
  commandUnitMapping?: { [commandUnitId: string]: string[] };
  shoulderNumberMappingField?: string;
  shoulderNumberValidationRegex?: string;
  graphGroupMapping?: { [graphGroupId: string]: string };
  groupSyncDelayMinutes?: number;
};

const hasValue = (value: string | undefined): boolean =>
  value !== undefined && value.trim().length > 0;

const graphApiCredentialsRequired = (data: {
  auth0ConnectionId?: string;
  tenantId?: string;
  clientId?: string;
  clientSecret?: string;
}): boolean =>
  hasValue(data.auth0ConnectionId) ||
  (hasValue(data.tenantId) &&
    hasValue(data.clientId) &&
    hasValue(data.clientSecret));

const graphApiCredentialsRequiredMessage =
  "tenantId, clientId, and clientSecret are required when auth0ConnectionId is not provided";

const graphGroupMappingRequiresGraphApi = (data: {
  auth0ConnectionId?: string;
  graphGroupMapping?: Record<string, string>;
}): boolean => {
  const hasGroupMapping =
    data.graphGroupMapping !== undefined &&
    Object.keys(data.graphGroupMapping).length > 0;
  if (!hasGroupMapping) return true;
  return !hasValue(data.auth0ConnectionId);
};

const graphGroupMappingRequiresGraphApiMessage =
  "graphGroupMapping requires Graph API credentials and cannot be used with auth0ConnectionId";

export const UserDirectoryInstanceSchema = z
  .object({
    id: UserDirectoryInstanceIdSchema,
    organisationId: OrganisationIdSchema,
    tenantId: looseUuid().optional(),
    clientId: looseUuid().optional(),
    clientSecret: z.string().optional(),
    accessToken: z.string(),
    accessTokenExpires: z.coerce.date(),
    userDirectoryKey: UserDirectoryKeysSchema,
    auth0ConnectionId: z.string().optional(),
    supervisorMapping: z.array(z.string()).optional(),
    commandUnitMapping: z.record(z.string(), z.array(z.string())).optional(),
    shoulderNumberMappingField: z.string().optional(),
    shoulderNumberValidationRegex: z.string().optional(),
    graphGroupMapping: z.record(looseUuid(), OrganisationIdSchema).optional(),
    groupSyncDelayMinutes: z.number().int().positive().optional(),
  })
  .refine(graphApiCredentialsRequired, {
    message: graphApiCredentialsRequiredMessage,
  })
  .brand<"vision-common:UserDirectoryInstance">() satisfies ZodType<UserDirectoryInstanceUnbranded>;

export type UserDirectoryInstance = z.infer<typeof UserDirectoryInstanceSchema>;

export type CreateUserDirectoryInstanceUnbranded = Omit<
  UserDirectoryInstanceUnbranded,
  "id" | "accessToken" | "accessTokenExpires" | "groupSyncDelayMinutes"
> & {
  groupSyncDelayMinutes?: number | null;
};

export type UpdateUserDirectoryInstanceUnbranded =
  Partial<CreateUserDirectoryInstanceUnbranded>;

export const CreateUserDirectoryInstanceSchema = z
  .object({
    organisationId: OrganisationIdSchema,
    tenantId: looseUuid().optional(),
    clientId: looseUuid().optional(),
    clientSecret: z.string().optional(),
    userDirectoryKey: UserDirectoryKeysSchema,
    auth0ConnectionId: z.string().optional(),
    supervisorMapping: z.array(z.string()).optional(),
    commandUnitMapping: z.record(z.string(), z.array(z.string())).optional(),
    shoulderNumberMappingField: z.string().optional(),
    shoulderNumberValidationRegex: z.string().optional(),
    graphGroupMapping: z.record(looseUuid(), OrganisationIdSchema).optional(),
    groupSyncDelayMinutes: z.number().int().positive().nullable().optional(),
  })
  .refine(graphApiCredentialsRequired, {
    message: graphApiCredentialsRequiredMessage,
  })
  .refine(graphGroupMappingRequiresGraphApi, {
    message: graphGroupMappingRequiresGraphApiMessage,
  })
  .brand<"vision-common:CreateUserDirectoryInstance">() satisfies ZodType<CreateUserDirectoryInstanceUnbranded>;

export type CreateUserDirectoryInstance = z.infer<
  typeof CreateUserDirectoryInstanceSchema
>;
